import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  BreadcrumbTrailContext,
  DEFAULT_TAB_LABEL_EVENT,
  readDefaultTabLabel,
  readTabLabel,
  readStoredTrail,
  writeStoredTrail,
  paramsFromPath,
  splitDestination,
  resolveBreadcrumbChain,
} from '../hooks/useBreadcrumbs';
import { BreadcrumbCrumb, CRUMB_LABELS, TrailCrumb } from '../screens/admin/types';

export type { TrailCrumb };
export { useBreadcrumbTrail } from '../hooks/useBreadcrumbs';

interface BreadcrumbTrailProviderProps {
  children: React.ReactNode;
}

export function BreadcrumbTrailProvider({ children }: BreadcrumbTrailProviderProps) {
  const location = useLocation();
  const [trail, setTrail] = useState<TrailCrumb[]>(() => readStoredTrail());
  const lastKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const currentKey = location.pathname + (location.search || '');
    if (lastKeyRef.current === currentKey) return;
    lastKeyRef.current = currentKey;
    const prevTrail = readStoredTrail();
    const currentSearchParams = new URLSearchParams(location.search);
    const activeTab = currentSearchParams.get('tab');
    const typeParam = currentSearchParams.get('type');
    const stepParam = currentSearchParams.get('step');
    const defaultTabLabel = readDefaultTabLabel(location.pathname);
    const staticChain = resolveBreadcrumbChain(location.pathname, activeTab, typeParam, defaultTabLabel, stepParam);
    if (!staticChain.length) {
      setTrail(prevTrail);
      return;
    }

    const [, ...pageChain] = staticChain as BreadcrumbCrumb[];

    const freshCrumbs: TrailCrumb[] = pageChain.map((c, i) => {
      const isLastOfVisit = i === pageChain.length - 1;
      const to = c.to || (isLastOfVisit ? currentKey : location.pathname);
      const key = isLastOfVisit ? currentKey : (c.to || location.pathname);
      const { path, queryParams } = splitDestination(to);
      return {
        label: c.label,
        to,
        key,
        pagePath: location.pathname,
        deptKey: c.deptKey,
        kind: c.kind,
        title: c.label,
        path,
        params: paramsFromPath(path),
        queryParams,
        tab: queryParams.tab || c.tab || (isLastOfVisit ? activeTab : null) || null,
        state: isLastOfVisit ? ((location.state as Record<string, unknown>) || {}) : {},
        id: key,
      };
    });

    setTrail((() => {
      const navState = location.state as Record<string, unknown> | null;
      if (navState?.__breadcrumbNav === true) {
        const idx = prevTrail.findIndex((c) => c.key === currentKey);
        if (idx > -1) {
          const truncated = prevTrail.slice(0, idx + 1);
          writeStoredTrail(truncated);
          return truncated;
        }
      }
      const isBareListPage = freshCrumbs.length <= 1;
      if (isBareListPage) {
        writeStoredTrail(freshCrumbs);
        return freshCrumbs;
      }
      let base = prevTrail;
      while (base.length && base[base.length - 1].pagePath === location.pathname) {
        base = base.slice(0, -1);
      }
      const { actionCreate, actionEdit, actionView } = CRUMB_LABELS;
      const lastFresh = freshCrumbs[freshCrumbs.length - 1];
      const isOnCreateOrEdit =
        lastFresh?.kind === 'action' &&
        (lastFresh.label === actionCreate || lastFresh.label === actionEdit ||
         lastFresh.label.startsWith(`${actionCreate} `) || lastFresh.label.startsWith(`${actionEdit} `));

      if (!isOnCreateOrEdit) {
        base = base.filter(
          c => !(c.kind === 'action' && (
            c.label === actionCreate || c.label === actionEdit ||
            c.label.startsWith(`${actionCreate} `) || c.label.startsWith(`${actionEdit} `)
          ))
        );
        while (base.length && base[base.length - 1].kind === 'page') {
          base = base.slice(0, -1);
        }
        const tail = base[base.length - 1];
        if (
          tail?.kind === 'action' &&
          (tail.label === actionView || tail.label.startsWith(`${actionView} (`) ||
           tail.label.startsWith('List (')) &&
          tail.key === lastFresh?.key
        ) {
          base = base.slice(0, -1);
        }
      }
      let nextTrail: TrailCrumb[];
      if (base.length === 0) {
        nextTrail = freshCrumbs;
      } else {
        const last = base[base.length - 1];
        const dupPrefix = last && freshCrumbs[0] && last.label === freshCrumbs[0].label;
        nextTrail = [...base, ...(dupPrefix ? freshCrumbs.slice(1) : freshCrumbs)];
      }
      writeStoredTrail(nextTrail);
      return nextTrail;
    })());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  useEffect(() => {
    function handleDefaultTabLabelReady(e: Event) {
      const { pathname: labelledPath, label } =
        (e as CustomEvent<{ pathname: string; label: string }>).detail || {};
      if (!labelledPath || !label || labelledPath !== location.pathname) return;
      const activeTab = new URLSearchParams(location.search).get('tab');
      if (activeTab && activeTab !== 'details') {
        const storedLabel = readTabLabel(location.pathname, activeTab);
        if (storedLabel !== label) return;
      }
      setTrail((prevTrail) => {
        if (!prevTrail.length) return prevTrail;
        const lastIdx = prevTrail.length - 1;
        const last = prevTrail[lastIdx];
        if (last.pagePath !== labelledPath || last.kind !== 'action') return prevTrail;
        if (last.label !== CRUMB_LABELS.actionView && !last.label.startsWith(`${CRUMB_LABELS.actionView} (`) && !last.label.startsWith('List (')) return prevTrail;
        if (last.label.includes(`(${label})`)) return prevTrail;
        const newLabel = `${CRUMB_LABELS.actionView} (${label})`;
        const patched: TrailCrumb = { ...last, label: newLabel, title: newLabel, tab: last.tab || 'details' };
        const nextTrail = [...prevTrail.slice(0, lastIdx), patched];
        writeStoredTrail(nextTrail);
        return nextTrail;
      });
    }
    document.addEventListener(DEFAULT_TAB_LABEL_EVENT, handleDefaultTabLabelReady);
    return () => document.removeEventListener(DEFAULT_TAB_LABEL_EVENT, handleDefaultTabLabelReady);
  }, [location.pathname, location.search]);

  return (
    <BreadcrumbTrailContext.Provider value={trail}>
      {children}
    </BreadcrumbTrailContext.Provider>
  );
}